import { useState, useRef } from "react";
import { Plus, Send, X, Mic, Image as ImageIcon, Type } from "lucide-react";
import { api } from "../lib/api";

interface Props {
  onCreated: () => void;
}

export function QuickAdd({ onCreated }: Props) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"text" | "voice" | "photo">("text");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  async function submitText() {
    if (!text.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      await api.createNote({ content: text.trim(), type: "text" });
      setText("");
      setOpen(false);
      onCreated();
    } catch (e: any) {
      setError(e.message || "Ошибка");
    } finally {
      setLoading(false);
    }
  }

  async function onVoice(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      await api.createVoiceNote(file);
      setOpen(false);
      onCreated();
    } catch (err: any) {
      setError(err.message || "Ошибка транскрипции");
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError("");
    try {
      const base64 = await fileToBase64(file);
      await api.createPhotoNote(base64, file.type);
      setOpen(false);
      onCreated();
    } catch (err: any) {
      setError(err.message || "Ошибка OCR");
    } finally {
      setLoading(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  }

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-accent shadow-lg shadow-accent/30 flex items-center justify-center active:scale-95 transition"
      >
        <Plus size={28} className="text-white" />
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 bg-surface-card border-t border-white/10 safe-bottom p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-white/80">Добавить</span>
        <button onClick={() => setOpen(false)} className="p-1 text-white/50">
          <X size={20} />
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        <ModeBtn active={mode === "text"} onClick={() => setMode("text")} icon={<Type size={16} />} label="Текст" />
        <ModeBtn active={mode === "voice"} onClick={() => setMode("voice")} icon={<Mic size={16} />} label="Голос" />
        <ModeBtn active={mode === "photo"} onClick={() => setMode("photo")} icon={<ImageIcon size={16} />} label="Фото" />
      </div>

      {mode === "text" && (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Кидай всё подряд..."
            rows={3}
            className="w-full bg-surface rounded-xl p-3 text-sm text-white placeholder:text-white/30 outline-none resize-none border border-white/10 focus:border-accent/50"
            autoFocus
          />
          <button
            onClick={submitText}
            disabled={loading || !text.trim()}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent-soft disabled:opacity-40 text-white font-medium py-3 rounded-xl transition"
          >
            {loading ? "Сохраняю..." : (
              <>
                <Send size={18} />
                Сохранить
              </>
            )}
          </button>
        </>
      )}

      {mode === "voice" && (
        <div className="text-center py-4">
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.ogg,.mp3,.wav,.m4a,.webm"
            className="hidden"
            onChange={onVoice}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            className="w-full bg-accent hover:bg-accent-soft disabled:opacity-40 text-white font-medium py-3 rounded-xl transition flex items-center justify-center gap-2"
          >
            {loading ? "Транскрибирую..." : (
              <>
                <Mic size={18} />
                Выбрать голосовое
              </>
            )}
          </button>
          <p className="text-xs text-white/30 mt-2">ogg, mp3, wav, m4a, webm</p>
        </div>
      )}

      {mode === "photo" && (
        <div className="text-center py-4">
          <input
            ref={photoRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onPhoto}
          />
          <button
            onClick={() => photoRef.current?.click()}
            disabled={loading}
            className="w-full bg-accent hover:bg-accent-soft disabled:opacity-40 text-white font-medium py-3 rounded-xl transition flex items-center justify-center gap-2"
          >
            {loading ? "Распознаю текст..." : (
              <>
                <ImageIcon size={18} />
                Выбрать фото / скрин
              </>
            )}
          </button>
          <p className="text-xs text-white/30 mt-2">AI извлечёт текст с изображения</p>
        </div>
      )}

      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
    </div>
  );
}

function ModeBtn({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition ${
        active
          ? "bg-accent/20 text-accent border border-accent/40"
          : "bg-surface text-white/50 border border-white/5"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
